import DataDogRouteWrapper from '@components/DataDogRouteWrapper';
import ErrorBoundary from '@components/error-boundary';
import IntercomCloseButton from '@components/IntercomCloseButton';
import ImageViewer from '@components/image-viewer';
import loadable from '@components/loadable';
import Navbar from '@components/navbar';
import PageNotFound from '@components/page-not-found';
import Sidebar from '@components/sidebar';
import Login from '@views/login';
import SingleSignOnCallback from '@views/single-sign-on-callback';
import SlcpFacilityRedirect from '@views/slcp-facility-redirect';
import SsoBridge from '@views/sso-bridge/routes/index';
import AuthorizedPage from '@/components/authorized-page';

const Unsubscribe = loadable(() => import('@views/unsubscribe'));

const authorizedRoutes = [
  {
    path: '/',
    component: loadable(() => import('@views/dashboard/dashboard')),
  },
  {
    path: '/logout',
    component: loadable(() => import('@views/logout')),
  },
  {
    path: '/onboarding/company-search',
    component: loadable(
      () => import('@views/company-directory/routes/no-company'),
    ),
  },
  {
    path: '/activities-feed/list',
    component: loadable(() => import('@views/activities-feed/list')),
  },
  {
    path: '/profile/view',
    component: loadable(() => import('@views/profile/view')),
  },
  {
    path: '/profile/settings',
    component: loadable(() => import('@views/profile/settings')),
  },
  {
    path: '/companies/list',
    component: loadable(
      () => import('@views/companies/supplier-network/supplier-network'),
    ),
  },
  {
    path: '/companies/create',
    component: loadable(() => import('@views/companies/create')),
  },
  {
    path: '/company-profiles/create',
    component: loadable(() => import('@views/company-profiles/create')),
  },
  {
    path: '/companies/:id/view',
    component: loadable(() => import('@views/companies/view')),
  },
  {
    path: '/integrations/amfori/history/:syncHistoryId',
    component: loadable(
      () => import('@views/integrations/routes/amfori-history'),
    ),
  },
  {
    path: '/companies/companies-network',
    component: loadable(
      () =>
        import(
          '@views/companies/supplier-network/company-network-graph/company-network'
        ),
    ),
  },
  {
    path: '/companies/:id/custom-information-fields/:fieldId/edit',
    component: loadable(
      () =>
        import(
          '@views/companies/custom-information-fields/custom-information-fields-edit'
        ),
    ),
  },
  {
    path: '/users/create',
    component: loadable(() => import('@views/users/create')),
  },
  {
    path: '/users/invite-coworker',
    component: loadable(() => import('@views/users/create')),
  },
  {
    path: '/users/:id/edit',
    component: loadable(() => import('@views/users/edit')),
  },
  {
    path: '/users/:id/view',
    component: loadable(() => import('@views/users/view')),
  },
  {
    path: '/products/list',
    component: loadable(() => import('@views/products/products')),
  },
  {
    path: '/products/list/archived',
    component: loadable(() => import('@views/products/archived')),
  },
  {
    path: '/product-components/list',
    component: loadable(() => import('@views/products/product-components')),
  },
  {
    path: '/product-components/list-archived',
    component: loadable(
      () => import('@views/products/product-components-archived'),
    ),
  },
  {
    path: '/product-components/create',
    component: loadable(() => import('@views/products/product-create')),
  },
  {
    path: '/product-components/:id/edit',
    component: loadable(() => import('@views/products/product-edit')),
  },
  {
    path: '/product-components/:id/view',
    component: loadable(() => import('@views/products/product-view')),
  },
  {
    path: '/products/create',
    component: loadable(() => import('@views/products/product-create')),
  },
  {
    path: '/products/:id/supply-chain',
    component: loadable(
      () => import('@views/products/supply-chain/product-supply-chain'),
    ),
  },
  {
    path: '/products/:id/bill-of-materials',
    component: loadable(
      () => import('@views/products/product-bill-of-materials'),
    ),
  },
  {
    path: '/products/:id/badges',
    component: loadable(() => import('@views/products/product-badges')),
  },
  {
    path: '/products/:id/webshop-preview',
    component: loadable(
      () => import('@views/products/product-webshop-preview'),
    ),
  },
  {
    path: '/products/:id/edit',
    component: loadable(() => import('@views/products/product-edit')),
  },
  {
    path: '/products/:id/view',
    component: loadable(() => import('@views/products/product-view')),
  },
  {
    path: '/products/:id/eco-footprint',
    component: loadable(() => import('@views/products/product-eco-footprint')),
  },
  {
    path: '/products/:id/qr-code',
    component: loadable(() => import('@views/products/product-qr-code')),
  },
  {
    path: '/products/:id/certifications',
    component: loadable(() => import('@views/products/product-certifications')),
  },
  {
    path: '/my-styles',
    component: loadable(() => import('@views/my-styles/index')),
  },
  {
    path: '/my-styles/styles/create',
    component: loadable(() => import('@views/my-styles/styles/create')),
  },
  {
    path: '/my-styles/styles/:id/edit',
    component: loadable(() => import('@views/my-styles/styles/edit')),
  },
  {
    path: '/my-styles/styles/:id/view',
    component: loadable(() => import('@views/my-styles/styles/view')),
  },
  {
    path: '/my-styles/variants/:id/view',
    component: loadable(() => import('@views/my-styles/variants/view')),
  },
  {
    path: '/my-styles/variants/:id/edit',
    component: loadable(() => import('@views/my-styles/variants/edit')),
  },
  {
    path: '/my-styles/skus/:id/view',
    component: loadable(() => import('@views/my-styles/skus/view')),
  },
  {
    path: '/my-styles/skus/:id/edit',
    component: loadable(() => import('@views/my-styles/skus/edit')),
  },
  {
    path: '/my-styles/variants/batches/:id/view',
    component: loadable(() => import('@views/my-styles/batches/view')),
  },
  {
    path: '/my-styles/styles/batches/:id/view',
    component: loadable(() => import('@views/my-styles/batches/view')),
  },
  {
    path: '/my-styles/skus/batches/:id/view',
    component: loadable(() => import('@views/my-styles/batches/view')),
  },
  {
    path: '/my-styles/styles/batches/:id/edit',
    component: loadable(() => import('@views/my-styles/batches/edit')),
  },
  {
    path: '/my-styles/variants/batches/:id/edit',
    component: loadable(() => import('@views/my-styles/batches/edit')),
  },
  {
    path: '/my-styles/skus/batches/:id/edit',
    component: loadable(() => import('@views/my-styles/batches/edit')),
  },
  {
    path: '/my-styles/styles/archive',
    component: loadable(() => import('@views/my-styles/styles/archive-list')),
  },
  {
    path: '/my-styles/styles/archive/:id/view',
    component: loadable(() => import('@views/my-styles/styles/view')),
  },
  {
    path: '/my-styles/styles/archive/:id/edit',
    component: loadable(() => import('@views/my-styles/styles/edit')),
  },
  {
    path: '/my-styles/variants/archive',
    component: loadable(() => import('@views/my-styles/variants/archive-list')),
  },
  {
    path: '/my-styles/variants/archive/:id/view',
    component: loadable(() => import('@views/my-styles/variants/view')),
  },
  {
    path: '/my-styles/variants/archive/:id/edit',
    component: loadable(() => import('@views/my-styles/variants/edit')),
  },
  {
    path: '/my-styles/skus/archive',
    component: loadable(() => import('@views/my-styles/skus/archive-list')),
  },
  {
    path: '/my-styles/skus/archive/:id/view',
    component: loadable(() => import('@views/my-styles/skus/view')),
  },
  {
    path: '/my-styles/skus/archive/:id/edit',
    component: loadable(() => import('@views/my-styles/skus/edit')),
  },
  {
    path: '/my-components',
    component: loadable(() => import('@views/my-components/index')),
  },
  {
    path: '/my-components/styles/create',
    component: loadable(() => import('@views/my-components/create')),
  },
  {
    path: '/my-components/list',
    component: loadable(() => import('@views/my-components/list')),
  },
  {
    path: '/my-components/styles/archive',
    component: loadable(() => import('@views/my-components/archive-list')),
  },
  {
    path: '/my-components/styles/archive/:id/view',
    component: loadable(() => import('@views/my-styles/styles/view')),
  },
  {
    path: '/my-components/styles/archive/:id/edit',
    component: loadable(() => import('@views/my-styles/styles/edit')),
  },
  {
    path: '/my-components/variants/archive',
    component: loadable(
      () => import('@views/my-components/variants/archive-list'),
    ),
  },
  {
    path: '/my-components/variants/archive/:id/view',
    component: loadable(() => import('@views/my-styles/variants/view')),
  },
  {
    path: '/my-components/variants/archive/:id/edit',
    component: loadable(() => import('@views/my-styles/variants/edit')),
  },
  {
    path: '/my-components/skus/archive',
    component: loadable(() => import('@views/my-components/skus/archive-list')),
  },
  {
    path: '/my-components/skus/archive/:id/view',
    component: loadable(() => import('@views/my-styles/skus/view')),
  },
  {
    path: '/my-components/skus/archive/:id/edit',
    component: loadable(() => import('@views/my-styles/skus/edit')),
  },
  {
    path: '/my-components/styles/:id/view',
    component: loadable(() => import('@views/my-styles/styles/view')),
  },
  {
    path: '/my-components/skus/:id/view',
    component: loadable(() => import('@views/my-styles/skus/view')),
  },
  {
    path: '/my-components/variants/:id/view',
    component: loadable(() => import('@views/my-styles/variants/view')),
  },
  {
    path: '/my-components/styles/:id/edit',
    component: loadable(() => import('@views/my-styles/styles/edit')),
  },
  {
    path: '/my-components/variants/:id/edit',
    component: loadable(() => import('@views/my-styles/variants/edit')),
  },
  {
    path: '/my-components/skus/:id/edit',
    component: loadable(() => import('@views/my-styles/skus/edit')),
  },
  {
    path: '/risk-landscape/view',
    component: loadable(
      () => import('@views/risk-analysis/risk-landscape-view'),
    ),
  },
  {
    path: '/risk-rules',
    component: loadable(() => import('@views/risk-analysis/risk-rules-list')),
  },
  {
    path: '/risk-rules/create',
    component: loadable(
      () => import('@views/risk-analysis/risk-rules-edit/risk-rules-edit'),
    ),
  },
  {
    path: '/risk-rules/:id/edit',
    component: loadable(
      () => import('@views/risk-analysis/risk-rules-edit/risk-rules-edit'),
    ),
  },
  {
    path: '/risk-rules/:id/view',
    component: loadable(
      () => import('@views/risk-analysis/risk-rules-view/risk-rules-view'),
    ),
  },
  {
    path: '/risk-assessments',
    component: loadable(
      () => import('@views/risk-analysis/risk-assessments-list'),
    ),
  },
  {
    path: '/risk-assessments/:id/view',
    component: loadable(
      () => import('@views/risk-analysis/risk-assessments-view'),
    ),
  },
  {
    path: '/risk-assessments/:id/details',
    component: loadable(
      () => import('@views/risk-analysis/risk-assessments-details'),
    ),
  },
  {
    path: '/risk-assessments/:id/add-entities',
    component: loadable(
      () =>
        import(
          '@views/risk-analysis/partials/risk-assessments/view/tabs/assessed-details/create'
        ),
    ),
  },
  {
    path: '/claims/list',
    component: loadable(() => import('@views/claims/list')),
  },
  {
    path: '/claims/create',
    component: loadable(() => import('@views/claims/create')),
  },
  {
    path: '/claims/:id/edit',
    component: loadable(() => import('@views/claims/edit')),
  },
  {
    path: '/claims/:id/view',
    component: loadable(() => import('@views/claims/view')),
  },
  {
    path: '/badges/list',
    component: loadable(() => import('@views/badges/list')),
  },
  {
    path: '/badges/:id',
    component: loadable(() => import('@views/badges/view')),
  },
  {
    path: '/standards/list',
    component: loadable(() => import('@views/standards/list')),
  },
  {
    path: '/standards/:id',
    component: loadable(() => import('@views/standards/view')),
  },
  {
    path: '/management/claims/list',
    component: loadable(() => import('@views/management/claims')),
  },
  {
    path: '/management/claims/:id/review',
    component: loadable(() => import('@views/management/claims/review')),
  },
  {
    path: '/management/claims/:id/view',
    component: loadable(() => import('@views/management/claims/view')),
  },
  {
    path: '/management/cs-tools',
    component: loadable(() => import('@views/management/cs-tools')),
  },
  {
    path: '/management/permissions',
    component: loadable(() => import('@views/management/permissions/routes')),
  },
  {
    path: '/management/users/list',
    component: loadable(() => import('@views/management/users/list')),
  },
  {
    path: '/management/users/:id/view',
    component: loadable(() => import('@views/management/users/view')),
  },
  {
    path: '/management/users/:id/edit',
    component: loadable(() => import('@views/management/users/edit')),
  },
  {
    path: '/management/companies/list',
    component: loadable(() => import('@views/management/companies/index')),
  },
  {
    path: '/management/companies/:id/view',
    component: loadable(() => import('@views/management/companies/view')),
  },
  {
    path: '/management/badges/list',
    component: loadable(() => import('@views/management/badges/list')),
  },
  {
    path: '/management/standards/list',
    component: loadable(
      () => import('@views/management/standards/standards-list'),
    ),
  },
  {
    path: '/management/risk-management',
    component: loadable(
      () => import('@views/management/risk-analysis/risk-analysis'),
    ),
  },
  {
    path: '/management/risk-management',
    component: loadable(
      () => import('@views/management/risk-analysis/risk-analysis'),
    ),
  },
  {
    path: '/management/risk-management/risk-rules/create',
    component: loadable(
      () =>
        import(
          '@views/management/risk-analysis/partials/risk-rules/edit/risk-rules-edit'
        ),
    ),
  },
  {
    path: '/management/risk-management/risk-rules/:id/edit',
    component: loadable(
      () =>
        import(
          '@views/management/risk-analysis/partials/risk-rules/edit/risk-rules-edit'
        ),
    ),
  },
  {
    path: '/management/platform-features/list',
    component: loadable(
      () => import('@views/management/platform-features/list'),
    ),
  },
  {
    path: '/management/development-tags/',
    component: loadable(
      () => import('@views/management/development-tags/list.jsx'),
    ),
  },
  {
    path: '/management/platform-features/:id/view',
    component: loadable(
      () => import('@views/management/platform-features/view'),
    ),
  },
  {
    path: '/management/question-templates',
    component: loadable(
      () => import('@views/management/question-templates/question-templates'),
    ),
  },
  {
    path: '/management/supplier-questionnaire-templates',
    component: loadable(
      () =>
        import(
          '@views/management/supplier-questionnaire-templates/supplier-questionnaire-templates'
        ),
    ),
  },
  {
    path: '/management/supplier-questionnaire-templates/:id/edit',
    component: loadable(
      () => import('@views/management/supplier-questionnaire-templates/edit'),
    ),
  },
  {
    path: '/management/slcp-dashboard',
    component: loadable(
      () => import('@views/management/slcp-dashboard/slcp-dashboard'),
    ),
  },
  {
    path: '/management/question-templates/edit/:id',
    component: loadable(
      () => import('@views/management/question-templates/edit'),
    ),
  },
  {
    path: '/management/question-templates/edit-category/:id',
    component: loadable(
      () => import('@views/management/question-templates/edit-category'),
    ),
  },
  {
    path: '/management/impersonate',
    component: loadable(() => import('@views/management/impersonate')),
  },

  {
    path: '/management/classification-mapping',
    component: loadable(
      () => import('@views/management/classification/classification'),
    ),
  },
  {
    path: '/management/classification-views',
    component: loadable(
      () =>
        import('@views/management/classification-views/classification-views'),
    ),
  },
  {
    path: '/management/classification-data-fields',
    component: loadable(
      () =>
        import(
          '@views/management/classification-data-fields/classification-data-fields'
        ),
    ),
  },
  {
    path: '/management/profile/edit',
    component: loadable(() => import('@views/management/profile/edit')),
  },
  {
    path: '/management/profile/settings',
    component: loadable(
      () => import('@views/management/profile/settings/settings'),
    ),
  },
  {
    path: '/management/materials/list',
    component: loadable(
      () => import('@views/management/materials/materials-list'),
    ),
  },
  {
    path: '/management/webshops/list',
    component: loadable(() => import('@views/management/webshops/list')),
  },
  {
    path: '/management/webshops/:id/view',
    component: loadable(() => import('@views/management/webshops/view')),
  },
  {
    path: '/management/origins/list',
    component: loadable(() => import('@views/management/origins/list')),
  },
  {
    path: '/management/product-categories/list',
    component: loadable(
      () => import('@views/management/product-categories/list'),
    ),
  },
  {
    path: '/management/product-categories/:id/fields',
    component: loadable(
      () => import('@views/management/product-categories/list-fields'),
    ),
  },
  {
    path: '/management/facility-processes/list',
    component: loadable(
      () => import('@views/management/facility-processes/list'),
    ),
  },
  {
    path: '/management/attachment-templates/list',
    component: loadable(
      () => import('@views/management/attachment-templates/routes/list'),
    ),
  },
  {
    path: '/management/audit-types/list',
    component: loadable(() => import('@views/management/audit-types/list')),
  },
  {
    path: '/management/document-types/list',
    component: loadable(() => import('@views/management/document-types/list')),
  },
  {
    path: '/management/audits/list',
    component: loadable(() => import('@views/management/audits/list')),
  },
  {
    path: '/management/documents/list',
    component: loadable(() => import('@views/management/documents/index')),
  },
  {
    path: '/management/attachment-templates/create',
    component: loadable(
      () => import('@views/management/attachment-templates/routes/create'),
    ),
  },
  {
    path: '/management/attachment-templates/:id/view',
    component: loadable(
      () => import('@views/management/attachment-templates/routes/view'),
    ),
  },
  {
    path: '/management/attachment-templates/:id/edit',
    component: loadable(
      () => import('@views/management/attachment-templates/routes/edit'),
    ),
  },
  {
    path: '/management/custom-information-fields/list',
    component: loadable(
      () => import('@views/management/custom-information-fields/list'),
    ),
  },
  {
    path: '/management/custom-information-fields/:id/edit',
    component: loadable(
      () => import('@views/management/custom-information-fields/edit'),
    ),
  },
  {
    path: '/management/production-processes/list',
    component: loadable(
      () => import('@views/management/production-processes/list'),
    ),
  },
  {
    path: '/management/supply-chains/:id/view',
    component: loadable(() => import('@views/management/supply-chains/view')),
  },
  {
    path: '/management/fiber-coc-models/list',
    component: loadable(
      () => import('@views/management/fiber/list-coc-models'),
    ),
  },
  {
    path: '/management/fiber-production-factors/list',
    component: loadable(
      () =>
        import('@views/management/fiber/production-factors/production-factors'),
    ),
  },
  {
    path: '/management/fiber-production-process/list',
    component: loadable(() => import('@views/management/fiber/processes/list')),
  },
  {
    path: '/management/fiber-settings',
    component: loadable(() => import('@views/management/fiber/settings')),
  },
  {
    path: '/management/tag-identifications/list',
    component: loadable(
      () => import('@views/management/tag-identifications/list'),
    ),
  },
  {
    path: '/management/relationship-types/list',
    component: loadable(
      () => import('@views/management/relationship-types/list'),
    ),
  },
  {
    path: '/management/collaboration-statuses/list',
    component: loadable(
      () => import('@views/management/collaboration-statuses/list'),
    ),
  },
  {
    path: '/management/rules/list',
    component: loadable(() => import('@views/management/rules/list')),
  },
  {
    path: '/product-chains/v1/list',
    component: loadable(() => import('@views/supply-chains/v1/list')),
  },
  {
    path: '/supply-chains/v1/:id',
    component: loadable(() => import('@views/supply-chains/v1/view')),
  },
  {
    path: '/supply-chains/v1/:id/view',
    component: loadable(() => import('@views/supply-chains/v1/view')),
  },
  {
    path: '/product-chains/v2',
    component: loadable(
      () => import('@views/supply-chains/v2/supply-chain-mapping'),
    ),
  },
  {
    path: '/product-chains/v2/list',
    component: loadable(
      () => import('@views/supply-chains/v2/supply-chain-mapping'),
    ),
  },
  {
    path: '/supply-chains/v2/:id/graph',
    component: loadable(
      () => import('@views/supply-chains/v2/info/supply-chain'),
    ),
  },
  {
    path: '/company-chains',
    component: loadable(() => import('@views/cascade-map/index')),
  },
  {
    path: '/company-chains/:id/graph',
    component: loadable(() => import('@views/cascade-map/info/supply-chain')),
  },
  {
    path: '/company-chains/:id/invite-collaborators',
    component: loadable(
      () => import('@views/cascade-map/invite-collaborators'),
    ),
  },
  {
    path: '/company-chains/:id/select-profiles',
    component: loadable(
      () =>
        import(
          '@views/cascade-map/partials/link-companies/select-profiles/list'
        ),
    ),
  },
  {
    path: '/company-chains/:id/confirm-profiles',
    component: loadable(
      () =>
        import(
          '@views/cascade-map/partials/link-companies/confirm-profiles/list'
        ),
    ),
  },
  {
    path: '/company-chains/:id/replace-company/select-profiles',
    component: loadable(
      () =>
        import(
          '@views/cascade-map/partials/replace-company/select-profiles/list'
        ),
    ),
  },
  {
    path: '/company-chains/:id/replace-company/confirm-profiles',
    component: loadable(
      () =>
        import(
          '@views/cascade-map/partials/replace-company/confirm-profiles/list'
        ),
    ),
  },
  {
    path: '/supply-chains/v2/archived',
    component: loadable(
      () => import('@views/supply-chains/v2/list/archive/archive'),
    ),
  },
  {
    path: '/company-chains/v2/archived',
    component: loadable(() => import('@views/cascade-map/index')),
  },
  {
    path: '/supply-chains/v2/:id/edit',
    component: loadable(() => import('@views/supply-chains/v2/edit/edit')),
  },
  {
    path: '/collaborative-product-mapping/list',
    component: loadable(
      () => import('@views/collaborative-product-mapping/routes/list'),
    ),
  },
  {
    path: '/collaborative-product-mapping/archived/list',
    component: loadable(
      () => import('@views/collaborative-product-mapping/routes/archived'),
    ),
  },
  {
    path: '/collaborative-product-mapping/:id/graph',
    component: loadable(
      () => import('@views/collaborative-product-mapping/routes/view'),
    ),
  },
  {
    path: '/entity-screening-v2',
    component: loadable(
      () => import('@views/entity-screening-v2/routes/list/index'),
    ),
  },
  {
    path: '/involved-orders/:companyId',
    component: loadable(
      () => import('@views/entity-screening-v2/routes/involved-orders/index'),
    ),
  },
  {
    path: '/transaction-certificate-management',
    component: loadable(
      () => import('@views/transaction-certificate-management/index'),
    ),
  },
  {
    path: '/audits-outdated',
    component: loadable(() => import('@views/audits-outdated/audits')),
  },
  {
    path: '/audits-outdated/create',
    component: loadable(() => import('@views/audits-outdated/create/create')),
  },
  {
    path: '/audits-outdated/:id/edit',
    component: loadable(() => import('@views/audits-outdated/edit/edit')),
  },
  {
    path: '/audits-outdated/:id/view',
    component: loadable(() => import('@views/audits-outdated/view')),
  },

  {
    path: '/audits',
    component: loadable(() => import('@views/audits/audits')),
  },
  {
    path: '/audits/create',
    component: loadable(() => import('@views/audits/create/create')),
  },
  {
    path: '/audits/:id/edit',
    component: loadable(() => import('@views/audits/edit/edit')),
  },
  {
    path: '/audits/:id/view',
    component: loadable(() => import('@views/audits/view')),
  },
  {
    path: '/audits-digitized/:id/view',
    component: loadable(() => import('@views/audits/routes/digitized-view')),
  },

  {
    path: '/company-documents/list',
    component: loadable(() => import('@views/company-documents/list')),
  },
  {
    path: '/cascade-tracing/purchase-orders/create',
    component: loadable(
      () => import('@views/cascade-tracing/purchase-orders/create'),
    ),
  },
  {
    path: '/cascade-tracing/purchase-orders/:id/view',
    component: loadable(
      () => import('@views/cascade-tracing/purchase-orders/view'),
    ),
  },
  {
    path: '/cascade-tracing/purchase-orders/:id/edit',
    component: loadable(
      () => import('@views/cascade-tracing/purchase-orders/edit'),
    ),
  },
  {
    path: '/cascade-tracing/purchase-orders/:id/tracing',
    component: loadable(
      () => import('@views/cascade-tracing/purchase-orders/tracing'),
    ),
  },
  {
    path: '/cascade-tracing/purchase-orders/:id/attachments',
    component: loadable(
      () => import('@views/cascade-tracing/purchase-orders/attachments'),
    ),
  },
  {
    path: '/cascade-tracing/purchase-orders/:id/products',
    component: loadable(
      () => import('@views/cascade-tracing/purchase-orders/products'),
    ),
  },
  {
    path: '/cascade-tracing',
    component: loadable(() => import('@views/cascade-tracing/cascade-tracing')),
  },
  {
    path: '/cascade-tracing/archived',
    component: loadable(
      () =>
        import('@views/cascade-tracing/archived-orders/archived-orders-list'),
    ),
  },
  {
    path: '/cascade-tracing/sales-orders/create',
    component: loadable(
      () => import('@views/cascade-tracing/sales-orders/create'),
    ),
  },
  {
    path: '/cascade-tracing/sales-orders/:id/view',
    component: loadable(
      () => import('@views/cascade-tracing/sales-orders/view'),
    ),
  },
  {
    path: '/cascade-tracing/sales-orders/:id/edit',
    component: loadable(
      () => import('@views/cascade-tracing/sales-orders/edit'),
    ),
  },
  {
    path: '/cascade-tracing/sales-orders/:id/tracing',
    component: loadable(
      () => import('@views/cascade-tracing/sales-orders/tracing'),
    ),
  },
  {
    path: '/cascade-tracing/sales-orders/:id/attachments',
    component: loadable(
      () => import('@views/cascade-tracing/sales-orders/attachments'),
    ),
  },
  {
    path: '/cascade-tracing/sales-orders/:id/products',
    component: loadable(
      () => import('@views/cascade-tracing/sales-orders/products'),
    ),
  },
  {
    path: '/cascade-tracing/internal-orders/:id/view',
    component: loadable(
      () => import('@views/cascade-tracing/internal-orders/view'),
    ),
  },
  {
    path: '/cascade-tracing/internal-orders/:id/tracing',
    component: loadable(
      () => import('@views/cascade-tracing/internal-orders/tracing'),
    ),
  },
  {
    path: '/cascade-tracing/internal-orders/:id/attachments',
    component: loadable(
      () => import('@views/cascade-tracing/internal-orders/attachments'),
    ),
  },
  {
    path: '/cascade-tracing/internal-orders/:id/products',
    component: loadable(
      () => import('@views/cascade-tracing/internal-orders/products'),
    ),
  },
  {
    path: '/cascade-tracing/internal-orders/list/list',
    component: loadable(
      () => import('@views/cascade-tracing/internal-orders/list/list'),
    ),
  },
  {
    path: '/cascade-tracing/file-center',
    component: loadable(
      () => import('@views/cascade-tracing/file-center/index'),
    ),
  },

  {
    path: '/fiber/transactions',
    component: loadable(() => import('@views/fiber/transactions/transactions')),
  },
  {
    path: '/fiber/transactions/outgoing/list',
    component: loadable(
      () => import('@views/fiber/transactions/outgoing/list'),
    ),
  },
  {
    path: '/fiber/transactions/outgoing/:id/view',
    component: loadable(
      () =>
        import(
          '@views/fiber/transactions/outgoing/transaction-details/transaction-details'
        ),
    ),
  },
  {
    path: '/fiber/transactions/incoming/list',
    component: loadable(
      () => import('@views/fiber/transactions/incoming/list'),
    ),
  },
  {
    path: '/fiber/transactions/incoming/:id/view',
    component: loadable(
      () =>
        import(
          '@views/fiber/transactions/incoming/transaction-details/transaction-details'
        ),
    ),
  },
  {
    path: '/fiber/transactions/incoming/create',
    component: loadable(
      () => import('@views/fiber/transactions/incoming/create'),
    ),
  },
  {
    path: '/fiber/transactions/outgoing/create',
    component: loadable(
      () => import('@views/fiber/transactions/outgoing/create'),
    ),
  },
  {
    path: '/fiber/transactions/incoming/receive/:id/view',
    component: loadable(
      () => import('@views/fiber/transactions/incoming/receive-incoming'),
    ),
  },
  {
    path: '/fiber/transformations',
    component: loadable(
      () => import('@views/fiber/transformations/transformations'),
    ),
  },
  {
    path: '/fiber/transformations/productions/list',
    component: loadable(
      () => import('@views/fiber/transformations/productions/list'),
    ),
  },
  {
    path: '/fiber/transformations/processes/list',
    component: loadable(
      () => import('@views/fiber/transformations/processes/list'),
    ),
  },
  {
    path: '/fiber/transformations/productions/:id/view',
    component: loadable(
      () => import('@views/fiber/transformations/productions/view'),
    ),
  },
  {
    path: '/fiber/transformations/productions/create',
    component: loadable(
      () => import('@views/fiber/transformations/productions/create'),
    ),
  },
  {
    path: '/fiber/inventory',
    component: loadable(() => import('@views/fiber/inventory/inventory')),
  },
  {
    path: '/fiber/inventory/products/list',
    component: loadable(() => import('@views/fiber/inventory/products/list')),
  },
  {
    path: '/fiber/inventory/components/list',
    component: loadable(() => import('@views/fiber/inventory/components/list')),
  },
  {
    path: '/fiber/inventory/product-details/:id/view',
    component: loadable(
      () => import('@views/fiber/inventory/product-details/view'),
    ),
  },
  {
    path: '/fiber/inventory/archived-lots',
    component: loadable(
      () => import('@views/fiber/inventory/lots/archived-list'),
    ),
  },
  {
    path: '/fiber/inventory/lots/:id/tracing',
    component: loadable(
      () => import('@views/fiber/inventory/lots/tracing/graph'),
    ),
  },
  {
    path: '/attachment-templates/list',
    component: loadable(
      () => import('@views/cascade-tracing/attachment-templates/list-v2'),
    ),
  },
  {
    path: '/attachment-templates/create',
    component: loadable(
      () => import('@views/cascade-tracing/attachment-templates/create-v2'),
    ),
  },
  {
    path: '/attachment-templates/:id/view',
    component: loadable(
      () => import('@views/cascade-tracing/attachment-templates/view'),
    ),
  },
  {
    path: '/attachment-templates/:id/v2/view',
    component: loadable(
      () => import('@views/cascade-tracing/attachment-templates/view-v2'),
    ),
  },
  {
    path: '/attachment-templates/:id/v2/edit',
    component: loadable(
      () => import('@views/cascade-tracing/attachment-templates/edit-v2'),
    ),
  },

  {
    path: '/capa-v2',
    component: loadable(() => import('@views/capa-v2')),
  },

  {
    path: '/capa-v2/actions/:id/details',
    component: loadable(() => import('@views/capa-v2/actions/details')),
  },
  {
    path: '/capa-v2/plans/:id/details',
    component: loadable(() => import('@views/capa-v2/plans/details')),
  },

  {
    path: '/certified-materials-management',
    component: loadable(() => import('@views/certified-materials/routes/list')),
  },

  {
    path: '/constants-mapping',
    component: loadable(
      () => import('@views/constants-mapping/constants-mapping'),
    ),
  },
  {
    path: '/api-keys',
    component: loadable(() => import('@views/api-keys/list/list')),
  },

  {
    path: '/questionnaires',
    component: loadable(() => import('@views/questionnaires/list')),
  },
  {
    path: '/questionnaires/:id/edit',
    component: loadable(() => import('@views/questionnaires/edit')),
  },
  {
    path: '/questionnaires/:id/preview',
    component: loadable(
      () => import('@views/questionnaires/questionnairePreview'),
    ),
  },
  {
    path: '/questionnaires/:id/invite-companies',
    component: loadable(
      () => import('@views/questionnaires/questionnaireTabs/InviteCompanies'),
    ),
  },
  {
    path: '/questionnaires/:id/select-profiles',
    component: loadable(
      () => import('@views/questionnaires/questionnaireTabs/SelectProfiles'),
    ),
  },
  {
    path: '/questionnaires/:id/answer',
    component: loadable(
      () => import('@views/questionnaires/answerQuestionnaireRequest'),
    ),
  },
  {
    path: '/questionnaires/archived',
    component: loadable(
      () => import('@views/questionnaires/archived-questionnaires-list/list'),
    ),
  },
  {
    path: '/reports/analytics/tracing-operations',
    component: loadable(
      () => import('@views/reports/tracing-operations/analytics'),
    ),
  },
  {
    path: '/reports/analytics/tracing-compliance',
    component: loadable(
      () => import('@views/reports/tracing-compliance/analytics'),
    ),
  },
  {
    path: '/reports/analytics/audit-compliance',
    component: loadable(
      () => import('@views/reports/audit-compliance/analytics'),
    ),
  },
  {
    path: '/reports/analytics/certificate-compliance',
    component: loadable(
      () => import('@views/reports/certificate-compliance/analytics'),
    ),
  },
  {
    path: '/slcp/assessments',
    component: loadable(() => import('@views/slcp/assessments')),
  },
  {
    path: '/slcp/assessments/:id',
    component: loadable(() => import('@views/slcp/assessments/id')),
  },
  {
    path: '/slcp/checkout/success',
    component: loadable(() => import('@views/slcp/checkout/success')),
  },
  {
    path: '/slcp/checkout/cancel',
    component: loadable(() => import('@views/slcp/checkout/cancel')),
  },
  {
    path: '/reports/analytics/certificate-compliance/drilldown/:chartType',
    component: loadable(
      () => import('@views/reports/certificate-compliance/drilldown'),
    ),
  },
  {
    path: '/management/files/list',
    component: loadable(() => import('@views/management/files/list')),
  },
  {
    path: '/management/files/:id/view',
    component: loadable(() => import('@views/management/files/view')),
  },
  {
    path: '/management/files/:id/chat',
    component: loadable(() => import('@views/management/files/chatWithFile')),
  },
  {
    path: '/reports/analytics/audit-compliance/drilldown/:chartType',
    component: loadable(
      () => import('@views/reports/audit-compliance/drilldown'),
    ),
  },
  {
    path: '/management/email-bounce-tracking/list',
    component: loadable(
      () => import('@views/management/email-bounce-tracking'),
    ),
  },
];

export const routerConfig = [
  {
    path: '/sso-bridge',
    element: <SsoBridge />,
  },
  {
    path: '/single-sign-on-callback',
    element: <SingleSignOnCallback />,
  },
  {
    path: '/slcp-facility',
    element: <SlcpFacilityRedirect />,
  },
  {
    path: '/login',
    element: (
      <>
        <Login />
        <IntercomCloseButton />
      </>
    ),
  },
  {
    path: '/unsubscribe',
    element: (
      <>
        <Unsubscribe />
      </>
    ),
  },
  {
    path: '/terms-of-use',
    element: (
      <AuthorizedPage
        path={'/terms-of-use'}
        component={loadable(() => import('@views/terms-of-use'))}
      />
    ),
  },
  ...authorizedRoutes.map(r => ({
    path: r.path,
    element: (
      <ErrorBoundary>
        <DataDogRouteWrapper>
          <Navbar />
          <Sidebar className='tw:!hidden tw:md:!block' />
          <AuthorizedPage path={r.path} component={r.component} />
          <ImageViewer />
          <IntercomCloseButton />
        </DataDogRouteWrapper>
      </ErrorBoundary>
    ),
  })),
  {
    path: '*',
    element: <PageNotFound />,
  },
];
